/**
 * Job detail header — displays fields, status badge, action buttons.
 * Clock in/out from any status. Complete only when clocked OUT.
 * Ship (complete→shipped), Archive (shipped→archived) with invoice modal.
 * Contact: click → selector panel (select existing or add new).
 * Cancel Job button → confirmation modal with optional reason.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { StatusBadge, ProgressBar, Button, SlideOutPanel, Modal } from '../common';
import {
  transitionJob, completeJob, shipJob, archiveJob, updateJob, cancelJob,
  clockIn, clockOut, getActiveClock, fetchCustomerContacts,
} from '../../api/jobs';
import { generateUnitSerials, updateUnitSerialNumber } from '../../api/files';
import { fetchContact, createContact, updateContact } from '../../api/customers';
import { useAuth } from '../../context/AuthContext';

const VALID_TRANSITIONS = {
  pending_bom:       ['bom_review'],
  bom_review:        ['waiting_on_quotes', 'ready_to_build'],
  waiting_on_quotes: ['quotes_received', 'waiting_on_parts'],
  quotes_received:   ['waiting_on_parts'],
  waiting_on_parts:  ['ready_to_build'],
  ready_to_build:    ['ready_to_ship'],
};

// Labor types for clock-in dropdown
const USER_LABOR_TYPES = [
  { value: 'standard', label: 'Standard Labor' },
  { value: 'testing', label: 'Testing' },
  { value: 'installation', label: 'Installation' },
];

// V2-a2 (partial): NEW clock-in selections are limited to the 2
// current design categories. Only used for the selection dropdown
// below (line ~385) — existing entries' labor_type_display is server-
// provided elsewhere in the UI, so legacy values still render.
const ADMIN_DESIGN_LABOR_TYPES = [
  { value: 'complete_system_drawing', label: 'Complete System Drawing' },
  { value: 'plc_programming', label: 'PLC Programming' },
];

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}
function fmtCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}
function fmtTimer(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function JobHeader({ job, onRefresh }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [actionLoading, setActionLoading] = useState(null);
  const [activeClock, setActiveClock] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Contact panel state
  const [contactPanelOpen, setContactPanelOpen] = useState(false);
  const [contactSelectorMode, setContactSelectorMode] = useState(true);
  const [contactForm, setContactForm] = useState({});
  const [contactSaving, setContactSaving] = useState(false);

  // Cancel modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Confirmation modals
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [confirmShipOpen, setConfirmShipOpen] = useState(false);

  // Clock in labor type dropdown
  const [clockInDropdownOpen, setClockInDropdownOpen] = useState(false);

  // Archive modal
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveForm, setArchiveForm] = useState({ invoice_date: '', invoice_number: '', true_cost: '' });

  // Fetch active clock
  useEffect(() => {
    if (!job) return;
    getActiveClock().then((data) => {
      setActiveClock(data && data.job === job.id ? data : null);
    }).catch(() => {});
  }, [job?.id, job?.status]);

  // Running timer
  useEffect(() => {
    if (!activeClock?.clock_in_at) { setElapsed(0); return; }
    const start = new Date(activeClock.clock_in_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [activeClock?.clock_in_at]);

  const loadContacts = useCallback(async () => {
    if (!job?.customer) return;
    try {
      const data = await fetchCustomerContacts({ customer: job.customer });
      setContacts(data.data || data.results || data || []);
    } catch { /* ignore */ }
  }, [job?.customer]);

  if (!job) return null;

  const isClocked = !!activeClock;
  const isReadyToShip = job.status === 'ready_to_ship';
  const isShipped = job.status === 'shipped';
  const isArchived = job.status === 'archived';
  const isCancelled = job.status === 'cancelled';
  const isTerminal = isReadyToShip || isShipped || isArchived || isCancelled;

  // --- Actions ---
  async function handleComplete() {
    if (isClocked) { alert('Please clock out before completing the job.'); return; }
    setActionLoading('complete');
    try {
      await completeJob(job.id);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Complete failed'); }
    finally { setActionLoading(null); }
  }

  async function handleShip() {
    setActionLoading('ship');
    try {
      await shipJob(job.id);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Ship failed'); }
    finally { setActionLoading(null); }
  }

  async function handleArchiveSubmit() {
    if (!archiveForm.invoice_date || !archiveForm.invoice_number || !archiveForm.true_cost) {
      alert('Invoice date, invoice number, and true cost are all required.');
      return;
    }
    setActionLoading('archive');
    try {
      await archiveJob(job.id, {
        invoice_date: archiveForm.invoice_date,
        invoice_number: archiveForm.invoice_number,
        true_cost: parseFloat(archiveForm.true_cost),
      });
      setArchiveModalOpen(false);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Archive failed'); }
    finally { setActionLoading(null); }
  }

  async function handleClockIn(laborType = 'standard') {
    setActionLoading('clock');
    setClockInDropdownOpen(false);
    try {
      const result = await clockIn(job.id, { laborType });
      setActiveClock(result);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Clock-in failed'); }
    finally { setActionLoading(null); }
  }

  async function handleClockOut() {
    setActionLoading('clock');
    try {
      await clockOut(job.id);
      setActiveClock(null);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Clock-out failed'); }
    finally { setActionLoading(null); }
  }

  async function handleToggleWarranty() {
    setActionLoading('warranty');
    try {
      await updateJob(job.id, { is_warranty: !job.is_warranty });
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update warranty status'); }
    finally { setActionLoading(null); }
  }

  async function handleCancelJob() {
    setActionLoading('cancel');
    try {
      await cancelJob(job.id, cancelReason);
      setCancelModalOpen(false);
      setCancelReason('');
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Cancel failed'); }
    finally { setActionLoading(null); }
  }

  // --- Edit mode ---
  function startEditing() {
    setEditForm({
      quantity: job.quantity,
      sell_price: job.sell_price || '',
      priority: job.priority || 'medium',
      customer_contact: job.customer_contact || '',
      target_due_date: job.target_due_date || '',
      customer_po_number: job.customer_po_number || '',
      workorder_number: job.workorder_number || '',
      sales_order_number: job.sales_order_number || '',
      quote_number: job.quote_number || '',
      notes: job.notes || '',
      paint_color: job.paint_color || '',
      is_design_job: job.is_design_job || false,
    });
    loadContacts();
    setEditing(true);
  }

  async function handleSaveEdit() {
    setSaving(true);
    try {
      const payload = { ...editForm };
      // Clean up empty strings — Django DateField/FK fields need null, not ''
      if (payload.sell_price === '') delete payload.sell_price;
      if (!payload.customer_contact) payload.customer_contact = null;
      if (!payload.target_due_date) payload.target_due_date = null;
      if (!payload.invoice_date) delete payload.invoice_date;
      if (payload.quantity) payload.quantity = parseInt(payload.quantity) || 1;
      if (payload.projected_labor_hours === '') payload.projected_labor_hours = null;
      await updateJob(job.id, payload);
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.error || (typeof errData === 'object' ? JSON.stringify(errData) : 'Save failed');
      alert(msg);
    }
    finally { setSaving(false); }
  }

  // --- Contact panel ---
  async function openContactPanel() {
    await loadContacts();
    setContactSelectorMode(true);
    setContactForm({ first_name: '', last_name: '', email: '', phone: '', role_title: '', is_primary: false });
    setContactPanelOpen(true);
  }

  async function selectContact(contactId) {
    try {
      await updateJob(job.id, { customer_contact: contactId || null });
      setContactPanelOpen(false);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || 'Failed to assign contact'); }
  }

  async function handleCreateContact() {
    setContactSaving(true);
    try {
      const created = await createContact({ ...contactForm, customer: job.customer });
      await updateJob(job.id, { customer_contact: created.id });
      setContactPanelOpen(false);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Save failed'); }
    finally { setContactSaving(false); }
  }

  const contactDisplay = job.contact_display
    || job.customer_snapshot?.contact_name
    || (job.customer_contact ? 'Contact' : 'No contact');

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Top row */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="text-gray-400 hover:text-gray-600" title="Back to queue">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-foreground">Job #{job.job_number}</h1>
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 uppercase">{job.job_type}</span>
                  <StatusBadge status={job.status} />
                  {job.needs_pulling_flag && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      Needs Pulling
                    </span>
                  )}
                  {/* Warranty toggle — available before completion */}
                  {!isTerminal && (
                    <button
                      onClick={handleToggleWarranty}
                      disabled={actionLoading === 'warranty'}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                        job.is_warranty
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      title={job.is_warranty ? 'Click to remove warranty status' : 'Click to mark as warranty'}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      {job.is_warranty ? 'Warranty' : 'Not Warranty'}
                    </button>
                  )}
                  {/* Show warranty badge for terminal statuses */}
                  {isTerminal && job.is_warranty && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Warranty
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{job.parent_item_description || job.work_description || 'No description'}</p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Clock in/out */}
              {!isTerminal && (
                isClocked ? (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-sm font-mono font-bold text-green-700">{fmtTimer(elapsed)}</span>
                    </div>
                    <Button variant="danger" size="sm" loading={actionLoading === 'clock'} onClick={handleClockOut}>Clock Out</Button>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setClockInDropdownOpen(!clockInDropdownOpen)}
                      disabled={actionLoading === 'clock'}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 hover:bg-green-700 text-white shadow-md transition-all disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Clock In
                      <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {clockInDropdownOpen && (
                      <>
                        {/* Backdrop to close dropdown */}
                        <div className="fixed inset-0 z-40" onClick={() => setClockInDropdownOpen(false)} />
                        {/* Dropdown menu */}
                        <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                          <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">Select Labor Type</div>
                          {USER_LABOR_TYPES.map((lt) => (
                            <button
                              key={lt.value}
                              onClick={() => handleClockIn(lt.value)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 hover:text-green-700 transition-colors"
                            >
                              {lt.label}
                            </button>
                          ))}
                          {/* Admin-only design labor types - only show if admin AND design job */}
                          {isAdmin && job.is_design_job && (
                            <>
                              <div className="border-t my-1" />
                              <div className="px-3 py-1.5 text-xs font-semibold text-purple-600 uppercase tracking-wider">Design Labor</div>
                              {ADMIN_DESIGN_LABOR_TYPES.map((lt) => (
                                <button
                                  key={lt.value}
                                  onClick={() => handleClockIn(lt.value)}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-700 transition-colors"
                                >
                                  {lt.label}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )
              )}

              {/* Complete Job — available when not terminal and not clocked in */}
              {!isTerminal && !isClocked && (
                <Button variant="primary" size="sm" onClick={() => setConfirmCompleteOpen(true)}>
                  Complete Job
                </Button>
              )}

              {/* Ship — available when ready_to_ship */}
              {isReadyToShip && (
                <Button variant="primary" size="sm" onClick={() => setConfirmShipOpen(true)}>Ship</Button>
              )}

              {/* Archive */}
              {isShipped && (
                <Button variant="danger" size="sm" onClick={() => {
                  setArchiveForm({ invoice_date: '', invoice_number: '', true_cost: '' });
                  setArchiveModalOpen(true);
                }}>Archive</Button>
              )}

              {/* Cancel Job — admin only, discreet text link */}
              {!isTerminal && isAdmin && (
                <button
                  onClick={() => { setCancelReason(''); setCancelModalOpen(true); }}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors px-1 py-1 rounded hover:bg-red-50"
                  title="Cancel this job (admin only)"
                >
                  Cancel Job
                </button>
              )}

              {/* Edit */}
              {!isArchived && !isCancelled && !editing && (
                <Button variant="ghost" size="sm" onClick={startEditing}>Edit</Button>
              )}
            </div>
          </div>
        </div>

        {/* Cancelled banner */}
        {isCancelled && (
          <div className="px-5 py-2 bg-red-50 border-b border-red-200 flex items-center gap-2">
            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <span className="text-sm font-medium text-red-700">
              Job cancelled {job.cancelled_at ? `on ${fmtDate(job.cancelled_at)}` : ''}
              {job.cancelled_reason && ` — ${job.cancelled_reason}`}
            </span>
          </div>
        )}

        {/* Status pipeline */}
        {!isCancelled && !isArchived && (
          <StatusPipeline status={job.status} jobType={job.job_type} />
        )}

        {/* Detail fields — edit or view mode */}
        {editing ? (
          <div className="px-5 py-4 space-y-3 bg-blue-50/30 border-b border-blue-100">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <EditField label="Quantity" type="number" value={editForm.quantity} onChange={(v) => setEditForm({ ...editForm, quantity: v })} />
              <EditField label="Sell Price" type="number" step="0.01" value={editForm.sell_price} onChange={(v) => setEditForm({ ...editForm, sell_price: v })} />
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Priority</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Contact</label>
                <div className="flex gap-1">
                  <select className="flex-1 border rounded px-2 py-1.5 text-sm" value={editForm.customer_contact} onChange={(e) => setEditForm({ ...editForm, customer_contact: e.target.value })}>
                    <option value="">None</option>
                    {contacts.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                  </select>
                </div>
              </div>
              <EditField label="Target Due Date" type="date" value={editForm.target_due_date} onChange={(v) => setEditForm({ ...editForm, target_due_date: v })} />
              <EditField label="Quote#" value={editForm.quote_number} onChange={(v) => setEditForm({ ...editForm, quote_number: v })} />
              <EditField label="Sales Order#" value={editForm.sales_order_number} onChange={(v) => setEditForm({ ...editForm, sales_order_number: v })} />
              <EditField label="Work Order#" value={editForm.workorder_number} onChange={(v) => setEditForm({ ...editForm, workorder_number: v })} />
              <EditField label="Customer PO#" value={editForm.customer_po_number} onChange={(v) => setEditForm({ ...editForm, customer_po_number: v })} />
              <EditField label="Paint Color" value={editForm.paint_color} onChange={(v) => setEditForm({ ...editForm, paint_color: v })} />
            </div>
            {/* Design Job checkbox — build jobs only */}
            {job.job_type === 'build' && (
              <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors col-span-full ${
                editForm.is_design_job ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'
              }`}>
                <input
                  type="checkbox"
                  id="edit_is_design_job"
                  checked={editForm.is_design_job}
                  onChange={(e) => setEditForm({ ...editForm, is_design_job: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="edit_is_design_job" className="cursor-pointer select-none">
                  <span className="text-sm font-medium text-gray-800">✏️ Design Job</span>
                  <p className="text-xs text-gray-500">Job is in active design — enables Design tab and design labor types.</p>
                </label>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" loading={saving} onClick={handleSaveEdit}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-4 text-sm border-b border-gray-100">
              <Field label="Customer" value={job.customer_name || '—'} />
              <div>
                <dt className="text-xs text-gray-400">Contact</dt>
                <dd>
                  <button onClick={openContactPanel} className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-sm">
                    {contactDisplay}
                  </button>
                </dd>
              </div>
              <div className="lg:col-span-2">
                <dt className="text-xs text-gray-400">Parent Item</dt>
                <dd className="font-medium text-sm text-foreground">
                  {job.parent_item_no
                    ? <><span className="font-semibold">{job.parent_item_no}</span>{job.parent_item_description ? <span className="text-gray-500 font-normal"> — {job.parent_item_description}</span> : ''}</>
                    : '—'}
                </dd>
              </div>
              <Field label="BOM Version" value={job.bom_version_display || '—'} />
              <Field label="Quantity" value={job.quantity ?? '—'} />
              <Field label="Sell Price/Unit" value={fmtCurrency(job.sell_price)} />
              <Field label="Ext Sell Price" value={fmtCurrency((parseFloat(job.sell_price) || 0) * (parseInt(job.quantity) || 1))} highlight={false} />
              <Field label="Priority" value={job.priority ? job.priority.charAt(0).toUpperCase() + job.priority.slice(1) : '—'} />
              <Field label="Target Due Date" value={fmtDate(job.target_due_date)} highlight={isOverdue(job)} />
              <Field label="Created Date" value={fmtDate(job.created_at)} />
              <Field label="Proj. Parts Ready" value={fmtDate(job.projected_parts_ready_date)} />
              {/* V2-b Stage B — when projected_completion_date is
                  NULL and the job is active, surface WHY via the
                  Field value instead of blank. 'awaiting_design'
                  wins; other NULL cases fall through to "Needs
                  estimate". Persisted date wins otherwise. */}
              <Field
                label="Proj. Completion"
                value={
                  job.projected_completion_date
                    ? fmtDate(job.projected_completion_date)
                    : isTerminal
                    ? '—'
                    : job.completion_projection_source === 'awaiting_design'
                    ? 'Awaiting design'
                    : 'Needs estimate'
                }
              />
              <Field label="Quote#" value={job.quote_number || '—'} />
              <Field label="Sales Order#" value={job.sales_order_number || '—'} />
              <Field label="Work Order#" value={job.workorder_number || '—'} />
              <Field label="Customer PO#" value={job.customer_po_number || '—'} />
              {job.paint_color && <Field label="Paint Color" value={job.paint_color} />}
              {job.invoice_number && <Field label="Invoice#" value={job.invoice_number} />}
              {job.invoice_date && <Field label="Invoice Date" value={fmtDate(job.invoice_date)} />}
              {job.true_cost != null && <Field label="True Cost" value={fmtCurrency(job.true_cost)} />}
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <dt className="text-xs text-gray-400 mb-0.5">Notes</dt>
              <dd className="text-sm text-gray-700 whitespace-pre-wrap min-h-[1.5rem]">
                {job.notes || <span className="text-gray-300 italic">None</span>}
              </dd>
            </div>
            <div className="px-5 py-3 grid grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500">Parts Readiness</span>
                  <span className="text-xs font-semibold text-gray-700">{(parseFloat(job.percent_parts_by_lines) || 0).toFixed(0)}%</span>
                </div>
                <ProgressBar value={parseFloat(job.percent_parts_by_lines) || 0} height="h-2" showLabel={false} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500">Labor Complete</span>
                  <span className="text-xs font-semibold text-gray-700">{(parseFloat(job.percent_labor_complete) || 0).toFixed(0)}%</span>
                </div>
                <ProgressBar value={parseFloat(job.percent_labor_complete) || 0} height="h-2" showLabel={false} />
              </div>
              {/* V2-a2 Part D — Design progress bar and projected date.
                  Renders only when the job has design activity
                  (server sets design_percent to null otherwise).
                  Spans both columns so it doesn't crowd the row above.
                  Rule matrix owned server-side in
                  serializers._compute_design_percent; frontend just
                  displays. Post-design jobs with no estimate show
                  actual hours instead of "100%". */}
              {(() => {
                if (job.design_percent == null) return null;
                const pct = parseFloat(job.design_percent);
                const projected = job.design_hours_projected != null
                  ? parseFloat(job.design_hours_projected) : 0;
                const actual = job.design_hours_actual != null
                  ? parseFloat(job.design_hours_actual) : 0;
                const showHoursOnly = pct === 100 && projected === 0;
                return (
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-purple-600">Design</span>
                      <span className="text-xs text-gray-500">
                        {showHoursOnly ? (
                          <span className="font-semibold text-purple-700">{actual.toFixed(1)}h</span>
                        ) : (
                          <>
                            <span className="font-semibold text-purple-700">{pct.toFixed(0)}%</span>
                            <span className="ml-2">({actual.toFixed(1)}h / {projected.toFixed(1)}h)</span>
                          </>
                        )}
                        {job.projected_design_completion_date && (
                          <span className="ml-3 text-purple-600">
                            Design done ~{fmtDate(job.projected_design_completion_date)}
                          </span>
                        )}
                      </span>
                    </div>
                    <ProgressBar value={pct} height="h-2" showLabel={false} color="purple" />
                  </div>
                );
              })()}
            </div>
            {/* Unit Serial Numbers */}
            <div className="px-5 py-3 border-t border-gray-100">
              <dt className="text-xs text-gray-400 mb-1.5">
                Unit Serial Numbers
                {isAdmin && job.unit_serial_numbers?.length > 0 && (
                  <span className="ml-2 text-[10px] text-gray-300">(click to edit)</span>
                )}
              </dt>
              {job.unit_serial_numbers && job.unit_serial_numbers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {job.unit_serial_numbers.map((usn) => (
                    <EditableSerialBadge
                      key={usn.id}
                      usn={usn}
                      editable={isAdmin}
                      onSaved={onRefresh}
                    />
                  ))}
                </div>
              ) : !isArchived && !isCancelled ? (
                <button
                  onClick={async () => {
                    try {
                      await generateUnitSerials(job.id);
                      onRefresh?.();
                    } catch (err) { alert(err.response?.data?.error || 'Failed to generate'); }
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Generate Unit Serial Numbers
                </button>
              ) : (
                <span className="text-sm text-gray-300">None</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Contact selector / add slide-out */}
      <SlideOutPanel
        open={contactPanelOpen}
        onClose={() => { setContactPanelOpen(false); setContactSelectorMode(true); }}
        title={contactSelectorMode ? 'Select Contact' : 'Add New Contact'}
        width="max-w-md"
      >
        {contactSelectorMode ? (
          <div className="space-y-3">
            {job.customer_contact && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
                <span className="font-medium">Currently assigned: </span>{contactDisplay}
              </div>
            )}
            {contacts.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-2">No contacts for this customer yet.</p>
            ) : (
              <div className="space-y-1.5">
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    className={`flex items-center justify-between rounded-md border px-3 py-2 ${
                      c.id === job.customer_contact
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {c.first_name} {c.last_name}
                        {c.is_primary && <span className="ml-1.5 text-xs text-blue-600">(Primary)</span>}
                      </div>
                      {(c.role_title || c.email) && (
                        <div className="text-xs text-gray-500">
                          {c.role_title}{c.role_title && c.email ? ' · ' : ''}{c.email}
                        </div>
                      )}
                    </div>
                    {c.id === job.customer_contact ? (
                      <span className="text-xs text-blue-600 font-medium">Selected</span>
                    ) : (
                      <button
                        onClick={() => selectContact(c.id)}
                        className="px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Select
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {job.customer_contact && (
              <button
                onClick={() => selectContact(null)}
                className="text-xs text-red-500 hover:text-red-700 underline"
              >
                Remove contact from job
              </button>
            )}
            <div className="pt-3 border-t border-gray-200">
              <Button size="sm" onClick={() => setContactSelectorMode(false)}>+ Add New Contact</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={() => setContactSelectorMode(true)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to contact list
            </button>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">First Name</label>
                <input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={contactForm.first_name || ''} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Last Name</label>
                <input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={contactForm.last_name || ''} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Email</label>
              <input type="email" className="w-full border rounded px-2 py-1.5 text-sm" value={contactForm.email || ''} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Phone</label>
              <input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={contactForm.phone || ''} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Title / Role</label>
              <input type="text" className="w-full border rounded px-2 py-1.5 text-sm" value={contactForm.role_title || ''} onChange={(e) => setContactForm({ ...contactForm, role_title: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={contactForm.is_primary || false} onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })} />
              Primary contact
            </label>
            <div className="flex gap-2 pt-2 border-t">
              <Button size="sm" loading={contactSaving} onClick={handleCreateContact}>Create & Select</Button>
              <Button size="sm" variant="ghost" onClick={() => setContactSelectorMode(true)}>Cancel</Button>
            </div>
          </div>
        )}
      </SlideOutPanel>

      {/* Complete confirmation modal */}
      <Modal open={confirmCompleteOpen} onClose={() => setConfirmCompleteOpen(false)} title="Complete Job" maxWidth="max-w-sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Are you sure you want to mark <strong>Job #{job.job_number}</strong> as complete? This will move it to Ready to Ship.</p>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => setConfirmCompleteOpen(false)}>Cancel</Button>
            <Button loading={actionLoading === 'complete'} onClick={() => { setConfirmCompleteOpen(false); handleComplete(); }}>Yes, Complete Job</Button>
          </div>
        </div>
      </Modal>

      {/* Ship confirmation modal */}
      <Modal open={confirmShipOpen} onClose={() => setConfirmShipOpen(false)} title="Ship Job" maxWidth="max-w-sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Are you sure you want to mark <strong>Job #{job.job_number}</strong> as shipped?</p>
          {job.is_warranty && (
            <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="text-xs text-amber-700">This is a <strong>warranty job</strong>. It will be automatically archived with no invoice required.</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => setConfirmShipOpen(false)}>Cancel</Button>
            <Button loading={actionLoading === 'ship'} onClick={() => { setConfirmShipOpen(false); handleShip(); }}>
              {job.is_warranty ? 'Ship & Archive' : 'Yes, Ship Job'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel job modal */}
      <Modal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} title="Cancel Job" maxWidth="max-w-md">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm text-red-700">
              <p className="font-medium">Cancel Job #{job.job_number}?</p>
              <p className="mt-1 text-red-600">This removes the job from the active queue. All time entries will be preserved but excluded from future labor calculations. This cannot be undone.</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <textarea
              className="w-full border rounded-md px-3 py-2 text-sm"
              rows={3}
              placeholder="Why is this job being cancelled?"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => setCancelModalOpen(false)}>Keep Job</Button>
            <Button variant="danger" loading={actionLoading === 'cancel'} onClick={handleCancelJob}>
              Yes, Cancel Job
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive modal */}
      <Modal open={archiveModalOpen} onClose={() => setArchiveModalOpen(false)} title="Archive Job" maxWidth="max-w-md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Enter invoice details and true cost to archive this job. Only archived jobs appear in P&L reports.</p>
          
          {/* Reference info for archiving */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div>
              <dt className="text-xs text-blue-600 font-medium">Sell Price/Unit</dt>
              <dd className="text-sm font-semibold text-gray-800">{fmtCurrency(job.sell_price)}</dd>
            </div>
            <div>
              <dt className="text-xs text-blue-600 font-medium">Quantity</dt>
              <dd className="text-sm font-semibold text-gray-800">{job.quantity || 1}</dd>
            </div>
            <div className="col-span-2 pt-2 border-t border-blue-200">
              <dt className="text-xs text-blue-600 font-medium">Ext Sell Price (Revenue)</dt>
              <dd className="text-lg font-bold text-blue-800">{fmtCurrency((parseFloat(job.sell_price) || 0) * (parseInt(job.quantity) || 1))}</dd>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date *</label>
            <input type="date" className="w-full border rounded-md px-3 py-2 text-sm" value={archiveForm.invoice_date} onChange={(e) => setArchiveForm({ ...archiveForm, invoice_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number *</label>
            <input type="text" className="w-full border rounded-md px-3 py-2 text-sm" value={archiveForm.invoice_number} onChange={(e) => setArchiveForm({ ...archiveForm, invoice_number: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">True Cost (Total for Job) *</label>
            <input type="number" step="0.01" className="w-full border rounded-md px-3 py-2 text-sm" placeholder="Total actual cost for entire job" value={archiveForm.true_cost} onChange={(e) => setArchiveForm({ ...archiveForm, true_cost: e.target.value })} />
            <p className="text-xs text-gray-500 mt-1">Enter the total cost for all {job.quantity || 1} unit{(job.quantity || 1) > 1 ? 's' : ''}.</p>
          </div>
          {/* Show computed margin preview */}
          {archiveForm.true_cost && (parseFloat(job.sell_price) || 0) > 0 && (() => {
            const extSell = (parseFloat(job.sell_price) || 0) * (parseInt(job.quantity) || 1);
            const trueCost = parseFloat(archiveForm.true_cost) || 0;
            const margin = extSell - trueCost;
            const marginPct = extSell > 0 ? (margin / extSell) * 100 : 0;
            return (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">True Margin:</span>
                  <span className={`text-lg font-bold ${marginPct > 20 ? 'text-green-600' : marginPct > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {fmtCurrency(margin)} ({marginPct.toFixed(1)}%)
                  </span>
                </div>
              </div>
            );
          })()}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" onClick={() => setArchiveModalOpen(false)}>Cancel</Button>
            <Button variant="danger" loading={actionLoading === 'archive'} onClick={handleArchiveSubmit}>Archive Job</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function EditableSerialBadge({ usn, editable, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(usn.serial_number);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    const trimmed = value.trim();
    if (!trimmed) { setValue(usn.serial_number); setEditing(false); return; }
    if (trimmed === usn.serial_number) { setEditing(false); return; }
    setSaving(true);
    try {
      await updateUnitSerialNumber(usn.id, { serial_number: trimmed });
      setEditing(false);
      onSaved?.();
    } catch (err) {
      alert(err.response?.data?.serial_number?.[0] || err.response?.data?.error || 'Save failed');
      setValue(usn.serial_number);
    } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 px-1 py-0.5 rounded-md bg-blue-100 border border-blue-300">
        <input
          type="text"
          className="w-24 px-1.5 py-0.5 text-sm font-mono font-bold text-blue-800 bg-white border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setValue(usn.serial_number); setEditing(false); } }}
          onBlur={handleSave}
          autoFocus
          disabled={saving}
        />
        <span className="text-[10px] text-blue-500">Unit {usn.unit_index}</span>
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 border border-blue-200 ${
        editable ? 'cursor-pointer hover:bg-blue-100 hover:border-blue-300 transition-colors' : ''
      }`}
      onClick={() => editable && setEditing(true)}
      title={editable ? 'Click to edit serial number' : ''}
    >
      <span className="text-sm font-bold text-blue-800 font-mono">{usn.serial_number}</span>
      <span className="text-[10px] text-blue-500">Unit {usn.unit_index}</span>
      {editable && (
        <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      )}
    </span>
  );
}

function Field({ label, value, highlight = false }) {
  return (
    <div>
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className={`font-medium text-sm ${highlight ? 'text-red-600' : 'text-foreground'}`}>{value}</dd>
    </div>
  );
}

function EditField({ label, type = 'text', step, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
      <input type={type} step={step} className="w-full border rounded px-2 py-1.5 text-sm" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function isOverdue(job) {
  if (!job.target_due_date || ['ready_to_ship', 'shipped', 'archived', 'cancelled'].includes(job.status)) return false;
  return new Date(job.target_due_date) < new Date();
}

// ── Status Pipeline ────────────────────────────────────────────────────────
const BUILD_STEPS = [
  { key: 'bom_review',        label: 'BOM Review' },
  { key: 'waiting_on_quotes', label: 'Waiting on Quotes' },
  { key: 'quotes_received',   label: 'Quotes In' },
  { key: 'waiting_on_parts',  label: 'Parts Ordered' },
  { key: 'ready_to_build',    label: 'Ready to Build' },
  { key: 'ready_to_ship',     label: 'Ready to Ship' },
  { key: 'shipped',           label: 'Shipped' },
];

const STATUS_STEP_MAP = {
  pending_bom:       'bom_review',
  bom_review:        'bom_review',
  waiting_on_quotes: 'waiting_on_quotes',
  quotes_received:   'quotes_received',
  waiting_on_parts:  'waiting_on_parts',
  ready_to_build:    'ready_to_ship',     // ready_to_build → shows steps 1-5 checked, 6-7 upcoming
  ready_to_ship:     'ready_to_ship',
  shipped:           'shipped',
  archived:          'shipped',
};

function StatusPipeline({ status, jobType }) {
  if (jobType === 'repair') return null;

  const activeKey = STATUS_STEP_MAP[status] || status;
  const activeIdx = BUILD_STEPS.findIndex((s) => s.key === activeKey);

  return (
    <div className="px-5 py-3 border-b border-gray-100 overflow-x-auto">
      <ol className="flex items-center min-w-max">
        {BUILD_STEPS.map((step, idx) => {
          const isDone    = idx < activeIdx;
          const isCurrent = idx === activeIdx && status !== 'ready_to_build';
          const isLast    = idx === BUILD_STEPS.length - 1;

          return (
            <li key={step.key} className="flex items-center">
              {/* Circle */}
              <div className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 text-[10px] font-bold border-2 ${
                isDone    ? 'bg-blue-600 border-blue-600 text-white' :
                isCurrent ? 'bg-white border-blue-600 text-blue-600' :
                            'bg-white border-gray-300 text-gray-400'
              }`}>
                {isDone ? (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </div>

              {/* Label */}
              <span className={`ml-1.5 text-xs whitespace-nowrap ${
                isCurrent ? 'font-semibold text-blue-700' :
                isDone    ? 'text-blue-500' :
                            'text-gray-400'
              }`}>
                {step.label}
              </span>

              {/* Connector */}
              {!isLast && (
                <div className={`mx-2 h-0.5 w-6 flex-shrink-0 ${
                  isDone ? 'bg-blue-500' : 'bg-gray-200'
                }`} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
